import { useState } from 'react';
import { Container } from '@/components/layout';
import { Heading, Text, Avatar } from '@/components/atoms';
import { PhotoGrid } from '../PhotoGrid';
import { CollectionGrid } from '../CollectionGrid';
import { LoadingState, ErrorState } from '@/components/composed';
import { useUserProfileGraphQL } from '@/graphql/hooks/useUsersGraphQL';

export interface UserProfileProps {
  username: string;
  className?: string;
}

type TabType = 'photos' | 'collections';

export function UserProfile({ username, className = '' }: UserProfileProps) {
  const [activeTab, setActiveTab] = useState<TabType>('photos');

  // Fetch user profile with photos and collections from GraphQL
  const { data, isLoading, error } = useUserProfileGraphQL(username);
  const { user, photos, collections, photoCount, collectionCount } = data;

  if (isLoading) {
    return <LoadingState message="Loading profile..." size="lg" />;
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'An error occurred while loading the user profile. Please try again.';
    return (
      <ErrorState
        title="Failed to load profile"
        message={errorMessage}
      />
    );
  }

  if (!user) {
    return (
      <ErrorState
        title="User not found"
        message={`User "${username}" does not exist.`}
      />
    );
  }

  return (
    <Container maxWidth="xl" className={className}>
      {/* User Header */}
      <div className="flex items-center gap-4 mb-8">
        <Avatar
          src={undefined}
          alt={user.displayName}
          fallback={user.displayName.substring(0, 2).toUpperCase()}
          size="xl"
        />
        <div>
          <Heading level="h1" className="mb-1">{user.displayName}</Heading>
          <Text variant="muted">
            @{user.username}{user.email && ` (${user.email})`}
          </Text>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab('photos')}
          className={`pb-3 px-2 transition-colors ${
            activeTab === 'photos'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted hover:text-foreground'
          }`}
        >
          <Text variant="body" className="font-medium">
            Photos ({photoCount})
          </Text>
        </button>
        <button
          onClick={() => setActiveTab('collections')}
          className={`pb-3 px-2 transition-colors ${
            activeTab === 'collections'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted hover:text-foreground'
          }`}
        >
          <Text variant="body" className="font-medium">
            Portfolios ({collectionCount})
          </Text>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'photos' && (
        <PhotoGrid
          photos={photos}
          userSlug={user.slug}
          isLoading={false}
        />
      )}

      {activeTab === 'collections' && (
        <CollectionGrid
          collections={collections}
          isLoading={false}
        />
      )}
    </Container>
  );
}
